<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import ModalBase from "@/components/modal/ModalBase.vue";
import ModalTabContent from "@/components/modal/ModalTabContent.vue";
import SectionCard from "@/components/SectionCard.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import SimpleTable from "@/components/table/SimpleTable.vue";
import CopyValue from "@/components/CopyValue.vue";
import TabsBootstrap from "@/components/TabsBootstrap.vue";
import UserTypeBadge from "@/components/badges/UserType.vue";
import StopDetailHistoryTable from "./StopDetailHistoryTable.vue";
import StopDocumentsTab from "./StopDocumentsTab.vue";
import { stopStore } from "@/_store/stopStore";
import { dateUtils, errorUtils } from "@/utils";
import {
  formatAddressLabel,
  formatCourier,
  formatHandlingType,
  formatStopTypeLabel,
  formatVolume,
  formatTimeWindow,
  compactClockLabel,
  formatTaskContentDescriptionTooltip,
  hasTaskContentDescription,
} from "../../../../utils/formatters";

const props = defineProps({
  modalId: { type: String, required: true },
  stopId: {
    type: String,
    required: true,
    default() {
      return null;
    },
  },
});

const emit = defineEmits(["close"]);

const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const stopsStore = stopStore();
const isLoading = ref(false);
const snackbar = ref(null);
const currentTab = ref("direccion");
const stopDetail = ref(buildEmptyStopDetail());
const detailTabs = [
  { label: "Dirección", value: "direccion" },
  { label: "Participantes", value: "participantes" },
  { label: "Bultos", value: "tareas" },
  { label: "Reparto", value: "estado" },
  { label: "Evidencias", value: "documentos" },
  { label: "Historial", value: "historial" },
];
const tabContentHeight = 215;

const contactCardTitle = computed(() =>
  stopDetail.value.type === "ENTREGA" ? "Destinatario" : "Remitente"
);

function extractDatePart(value) {
  if (typeof value !== "string") return "";
  return value.slice(0, 10);
}

function formatDeliveryDate(value) {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  return dateUtils.formatUtcDate(text, "DD/MM/YYYY HH:mm");
}

function formatDisplayValue(value) {
  const text = String(value ?? "").trim();
  return text || "-";
}

function formatDeliveryStatusTooltip(deliveryInfo) {
  const status = String(deliveryInfo?.status || "").trim().toUpperCase();
  const description = String(deliveryInfo?.status_description || "").trim();

  if (status === "INCIDENCIA" && description) {
    return `Incidencia: ${description}`;
  }

  return "";
}

function formatStopSchedule(stopDetailValue) {
  const window1 = formatTimeWindow(
    stopDetailValue?.time_window_1,
    compactClockLabel
  );
  const window2 = formatTimeWindow(
    stopDetailValue?.time_window_2,
    compactClockLabel
  );
  return [window1, window2].filter(Boolean).join(" | ");
}

function formatNormalizedAddress(stopDetailValue) {
  return formatAddressLabel(stopDetailValue?.address_normalized);
}

function getTaskStatusTooltip(taskItem) {
  const status = String(taskItem?.delivery_info?.status || "").toUpperCase();
  const description = String(taskItem?.delivery_info?.status_description || "").trim();

  if (status === "INCIDENCIA" && description) {
    return `Incidencia: ${description}`;
  }

  return "";
}


function buildEmptyStopDetail() {
  return {
    type: "",
    number: "",
    date: "",
    note_1: "",
    delivery_info: {
      attempts: 0,
      observations: "",
      status: "",
      status_description: "",
      date: null,
      user: "",
      documents: [],
    },
    contact: {
      name: "",
      email: "",
      attention_of: "",
      phone: "",
    },
    shipper_info: {
      name: "",
      vat_number: "",
      address: { street: "" },
    },
    address: {
      text: "",
    },
    address_normalized: {
      address: "",
      input_address: "",
      label: "",
    },
    time_window_1: {
      start_time: "",
      end_time: "",
    },
    time_window_2: {
      start_time: "",
      end_time: "",
    },
    tasks: [],
  };
}

const taskHeaders = [
  { header: "Código de barras", field: "barcode" },
  { header: "Kg", field: "weight", position: "start" },
  { header: "m³", field: "volumetric_weight", position: "start" },
  { header: "Dimensiones", field: "measurements", position: "start" },
  { header: "Manipulación", field: "handling_type", position: "start" },
  { header: "Naturaleza", field: "content_description", position: "center" },
  { header: "Transportista", field: "courier", position: "start" },
  { header: "Reparto", field: "delivery_info.status", width: "w-1", position: "center" },
];

function getTasksRows() {
  const data = Array.isArray(stopDetail.value?.tasks)
    ? stopDetail.value.tasks
    : [];
  return {
    total: data.length,
    data,
  };
}

function getPosition(field) {
  const column = taskHeaders.find((col) => col.field === field);
  return column?.position || "start";
}
function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo cargar el detalle de la parada",
    type: "danger",
    color: "white",
  };
}

function loadStopDetail() {
  isLoading.value = true;
  closeSnackbar();
  stopsStore.getStopById(props.stopId);
}

function handleHide() {
  currentTab.value = "direccion";
  emit("close");
}

watch(
  () => stopsStore.stop,
  (value) => {
    if (!value) return;
    stopDetail.value = value;
    isLoading.value = false;
  }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!value) return;
    showError(
      errorUtils.getErrorMessageHtml(
        value,
        "No se pudo cargar el detalle de la parada"
      )
    );
    stopDetail.value = buildEmptyStopDetail();
    isLoading.value = false;
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  loadStopDetail();
});
</script>

<template>
  <modal-base :id="modalId" width="60rem" @hide="handleHide">
    <template #header>
      <h6 class="mb-0">Detalle</h6>
    </template>

    <template #body>
      <div class="stop-detail-modal">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="stop-detail-date" class="col-form-label">Fecha</label>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ extractDatePart(stopDetail.date) }}</span>
          </div>
        </div>
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="stop-detail-number" class="col-form-label"
              >Número</label
            >
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ stopDetail.number }}</span>
          </div>
        </div>
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="stop-detail-type" class="col-form-label">Tipo</label>
          </div>
          <div class="col modal-form-value modal-form-value--auto">
            <user-type-badge
              style="min-width: 100px"
              class="badge badge-md"
              :item="formatStopTypeLabel(stopDetail)"
            />
          </div>
        </div>

      

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="stop-detail-window1" class="col-form-label"
              >Horario</label
            >
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ formatStopSchedule(stopDetail) }}</span>
          </div>
        </div>
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="stop-detail-note-1" class="col-form-label"
              >Observaciones</label
            >
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ formatDisplayValue(stopDetail.note_1) }}</span>
          </div>
        </div>
        <tabs-bootstrap
          v-model="currentTab"
          :tabs="detailTabs"
          size="sm"
          refined
          style-nav="nav-underline" 
        />

        <modal-tab-content :height="`${tabContentHeight}px`">
        <div v-if="currentTab === 'participantes'" class="row">
          <div class="col-6">
            <section-card title="Cargador">
              <div class="row modal-form-row modal-form-row--compact-top">
                <div class="modal-form-label">
                  <label class="col-form-label">Razón social</label>
                </div>
                <div class="col modal-form-value">
                  <span class="modal-display-value">{{ stopDetail.shipper_info?.name || '' }}</span>
                </div>
              </div>

              <div class="row modal-form-row">
                <div class="modal-form-label">
                  <label class="col-form-label">NIF</label>
                </div>
                <div class="col modal-form-value">
                  <span class="modal-display-value">{{ stopDetail.shipper_info?.vat_number || '' }}</span>
                </div>
              </div>

              <div class="row modal-form-row mb-0">
                <div class="modal-form-label">
                  <label class="col-form-label">Domicilio</label>
                </div>
                <div class="col modal-form-value">
                  <span class="modal-display-value">{{ stopDetail.shipper_info?.address?.street || '' }}</span>
                </div>
              </div>
            </section-card>
          </div>

          <div class="col-6">
            <section-card :title="contactCardTitle">
              <div class="row modal-form-row modal-form-row--compact-top">
                <div class="modal-form-label">
                  <label for="stop-detail-contact-name" class="col-form-label"
                    >Nombre</label
                  >
                </div>
                <div class="col modal-form-value">
                  <span class="modal-display-value">{{ stopDetail.contact?.name || '' }}</span>
                </div>
              </div>

              <div class="row modal-form-row">
                <div class="modal-form-label">
                  <label
                    for="stop-detail-contact-attention-of"
                    class="col-form-label"
                    >Contacto</label
                  >
                </div>
                <div class="col modal-form-value">
                  <span class="modal-display-value">{{ stopDetail.contact?.attention_of || '' }}</span>
                </div>
              </div>

              <div class="row modal-form-row">
                <div class="modal-form-label">
                  <label for="stop-detail-contact-phone" class="col-form-label"
                    >Teléfono</label
                  >
                </div>
                <div class="col modal-form-value">
                  <span class="modal-display-value">{{ stopDetail.contact?.phone || '' }}</span>
                </div>
              </div>

              <div class="row modal-form-row mb-0">
                <div class="modal-form-label">
                  <label for="stop-detail-contact-email" class="col-form-label"
                    >Email</label
                  >
                </div>
                <div class="col modal-form-value">
                  <span class="modal-display-value">{{ stopDetail.contact?.email || '' }}</span>
                </div>
              </div>
            </section-card>
          </div>
        </div>

        <div v-else-if="currentTab === 'direccion'">
          <alert-message title="Dirección declarada" type="info" class="mb-2 mt-3">
            {{ stopDetail.address?.text }}
          </alert-message>

          <div class="row modal-form-row modal-form-row--compact-top">
            <div class="modal-form-label">
              <label for="stop-detail-address-normalized" class="col-form-label"
                >Normalizada</label
              >
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatNormalizedAddress(stopDetail) }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="currentTab === 'estado'">
          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label for="stop-detail-delivery-status" class="col-form-label"
                >Estado</label
              >
            </div>
            <div class="col modal-form-value modal-form-value--auto">
                <argon-status
                  id="stop-detail-delivery-status"
                  entity="route_stop"
                  :status="stopDetail.delivery_info?.status || 'PENDIENTE'"
                  :tooltip-external-text="formatDeliveryStatusTooltip(stopDetail.delivery_info)"
                  width="100px"
                />
              </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label for="stop-detail-delivery-attempts" class="col-form-label"
                >Intentos</label
              >
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatDisplayValue(stopDetail.delivery_info?.attempts) }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label for="stop-detail-delivery-document" class="col-form-label"
                >Id fiscal</label
              >
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ stopDetail.delivery_info?.identity_document || "-" }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label for="stop-detail-delivery-observations" class="col-form-label"
                >Observaciones</label
              >
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatDisplayValue(stopDetail.delivery_info?.observations) }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label for="stop-detail-delivery-date" class="col-form-label"
                >Fecha</label
              >
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatDeliveryDate(stopDetail.delivery_info?.date) }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <label for="stop-detail-delivery-user" class="col-form-label"
                >Mensajero</label
              >
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ formatDisplayValue(stopDetail.delivery_info?.user) }}</span>
            </div>
          </div>

        </div>

        <div v-else-if="currentTab === 'tareas'">
          <simple-table
            :headers="taskHeaders"
            :items="getTasksRows()"
            :empty-message="'Sin tasks'"
            :show-line-separator="true"
            :height="tabContentHeight"
          >
            <template #default="{ item }">
              <argon-td
                :text-primary="item.barcode ?? ''"
                :position="getPosition('barcode')"
              >
                <template v-if="item.barcode" #append>
                  <copy-value :value-to-copy="item.barcode" size="xs" show-on-hover />
                </template>
              </argon-td>
              <argon-td
                :text-primary="item.weight ?? ''"
                :position="getPosition('weight')"
              />
              <argon-td
                :text-primary="formatVolume(item.volumetric_weight, 3)"
                :position="getPosition('volumetric_weight')"
              />
              <argon-td
                :text-primary="item.measurements ?? ''"
                :position="getPosition('measurements')"
              />
              <argon-td
                :text-primary="formatHandlingType(item.handling_type)"
                :position="getPosition('handling_type')"
              />
              <argon-td :position="getPosition('content_description')">
                <i
                  v-if="hasTaskContentDescription(item)"
                  v-tooltip="formatTaskContentDescriptionTooltip(item)"
                  class="fa-solid fa-circle-info grid-icon-primary grid-icon-info"
                  aria-label="Ver naturaleza de la mercancía"
                />
              </argon-td>
              <argon-td
                :text-primary="formatCourier(item.courier)"
                :position="getPosition('courier')"
              />
              <argon-td :position="getPosition('delivery_info.status')">
                <argon-status
                  entity="task"
                  :status="item.delivery_info?.status"
                  :tooltip-external-text="getTaskStatusTooltip(item)"
                  size="sm"
                />
              </argon-td>
            </template>
          </simple-table>
        </div>

        <div v-else-if="currentTab === 'documentos'">
          <stop-documents-tab
            :documents="stopDetail.delivery_info?.documents ?? []"
            :stop-date="stopDetail.date"
          />
        </div>

        <div v-else>
          <stop-detail-history-table
            :stop-id="stopId"
            :is-active="currentTab === 'historial'"
            :height="tabContentHeight"
          />
        </div>
        </modal-tab-content>
      </div>
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>

